import React, { useEffect, useState } from 'react';
import { Spin, Typography, Card, Row, Col, Image, Rate, List, Divider, Space, theme, Button, Modal } from 'antd';
import { message } from '../../services/toastMessage';
import { motion } from 'framer-motion';
import {
  IconMapPin, IconKey,
  IconMoodSad, IconStar, IconFileText, IconClock, IconCircleCheck, IconAlertTriangle, IconBan, IconUsers,
  IconCalendarOff, IconFlag, IconWeight, IconGauge, IconArrowsMaximize, IconTag, IconCalendarStats,
  IconEngine, IconSteeringWheel, IconUserCog, IconUserBolt, IconTruckDelivery, IconCoin,
  IconGasStation, IconRulerMeasure, IconPlugConnected,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import AppLayout from '../../components/Layouts/Layout';
import LandingLayout from '../../components/Layouts/LandingLayout';
import { MOBILE_BOTTOM_NAV_HEIGHT } from '../../components/Layouts/MobileBottomNav';
import BackButton from '../../components/BackButton';
import { useAuth } from '../../contextProviders/AuthProvider';
import EmptyState from '../../components/EmptyState';
import ReportModal from '../../components/ReportModal';
import { getHeavyDutyMachineryById } from '../../services/HeavyDutyMachineryService';
import { getReviewsForSpace } from '../../services/ReviewService';
import { IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { IReview } from '../../interfaces/heavyDutyMachinery/IReview';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import {
  ACCESS_TYPE_OPTIONS, CANCELLATION_POLICY_OPTIONS, HeavyDutyMachineryOption,
} from '../../constants/heavyDutyMachinerySpaceOptions';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { PROPULSION_TYPE_OPTIONS, FUEL_TYPE_OPTIONS } from '../../constants/machineryGlobalSpecOptions';
import { getCustomSpecFields } from '../../constants/machineryCustomSpecFieldsByType';
import { CANCELLATION_WINDOW_HOURS } from '../../helpers/bookingHelpers';
import { PillTone, HEAVY_DUTY_MACHINERY_SPACE_STATUS_TONES, HEAVY_DUTY_MACHINERY_SPACE_STATUS_LABEL_KEYS, MACHINERY_LIVE_STATUS_TONES, MACHINERY_LIVE_STATUS_LABEL_KEYS, pillToneStyle } from '../../theme/creamyTokens';
import { isPublicMachineryTelemetry } from '../../interfaces/heavyDutyMachinery/ITelemetry';
import { useUserProfile } from '../../contextProviders/UserProfileProvider';
import { useFxConvertedAmount } from '../../hooks/useFxConvertedAmount';
import { formatCurrency } from '../../helpers/currencyHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { useScrollChrome } from '../../hooks/useScrollChrome';

const { Title, Paragraph, Text } = Typography;

const MAX_GALLERY_THUMBNAILS = 4;

/** Coral, rounded "Reservar" CTA shared by both entry points on this page
 * (top, by the price; bottom, under the exact address) -- same soft
 * hover/tap motion as PillButton, on the app's own brand coral (see
 * theme/appTheme). */
const ReserveButton: React.FC<React.ComponentProps<typeof Button>> = ({ children, style, ...props }) => (
  <motion.div
    style={{ display: 'inline-block', width: props.block ? '100%' : undefined }}
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.98 }}
    transition={{ duration: 0.15 }}
  >
    <Button type="primary" shape="round" style={style} {...props}>
      {children}
    </Button>
  </motion.div>
);

const STATUS_ICONS: Record<string, React.ElementType> = {
  Draft: IconFileText,
  PendingVerification: IconClock,
  Active: IconCircleCheck,
  PendingImprovement: IconAlertTriangle,
  Suspended: IconBan,
};

const LIVE_STATUS_ICONS: Record<string, React.ElementType> = {
  Available: IconCircleCheck,
  Rented: IconEngine,
  Maintenance: IconAlertTriangle,
};

/** Pill badge (status + "Verificado") sharing the same soft semantic
 * background/radius as HeavyDutyMachineryCard/Settings/toasts -- see
 * creamyTokens.ts. */
const Pill: React.FC<{ tone: PillTone; icon: React.ReactNode; children: React.ReactNode }> = ({ tone, icon, children }) => {
  const { token } = theme.useToken();
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 14px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        ...pillToneStyle(tone, token),
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const HeavyDutyMachineryDetail: React.FC = () => {
  const { token } = theme.useToken();
  const { t, i18n } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  // Mirrors MobileBottomNav's own hide-on-scroll-down/show-on-scroll-up so
  // the floating "Reservar ahora" bar below moves together with that bar --
  // both hidden while the guest is actively reading (scrolling down), both
  // back the moment they scroll up even a little.
  const { scrollingDown } = useScrollChrome();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, loading: authLoading, userId } = useAuth();
  const { resolveName: resolveCategoryName } = useTaxonomy();
  const { userProfile } = useUserProfile();
  const [space, setSpace] = useState<IHeavyDutyMachinery | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<IReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reportSpaceOpen, setReportSpaceOpen] = useState(false);
  // Set means "reporting this specific photo" (its own URL, see ReportModal's
  // own doc comment on why that's the TargetId for Photo) -- undefined means
  // the picker below is closed. Picking one closes the picker and opens the
  // shared ReportModal on it in the same click, rather than a separate step.
  const [reportPhotoPickerOpen, setReportPhotoPickerOpen] = useState(false);
  const [reportPhotoUrl, setReportPhotoUrl] = useState<string | undefined>(undefined);

  // Hooks can't sit below the loading/not-found early returns further down,
  // so this is computed here even though `space` may still be undefined --
  // useFxConvertedAmount already treats a missing amount/currency as
  // "nothing to convert".
  const rawPriceAmount = space ? (space.rentalMode === 'Overnight' ? space.pricePerNight : space.pricePerHour) : undefined;
  const convertedPriceAmount = useFxConvertedAmount(rawPriceAmount, space?.hostCurrencyCode, userProfile?.preferredCurrencyCode);

  // Public, no-auth space detail (see AppRoutes.tsx) -- AppLayout blanks the
  // page for anonymous visitors (its own `if (!isAuthenticated) return
  // null`), so reuse LandingLayout's own header+footer chrome instead when
  // signed out (same as the homepage), rather than an unstyled div with no
  // header/footer. LandingLayout's content area is centered, which this
  // page's cards/grids don't want -- textAlign: 'left' below overrides that
  // inherited value. Booking itself lives on its own dedicated route/page
  // now (see the "Reservar"/"Reservar ahora" CTAs below and
  // HeavyDutyMachineryBookingPage's own doc comment), gated inline there.
  const withLayout = (node: React.ReactNode) => {
    if (authLoading) return null;
    if (!isAuthenticated) return <LandingLayout>{node}</LandingLayout>;
    return <AppLayout>{node}</AppLayout>;
  };

  useEffect(() => {
    if (!id) return;
    getHeavyDutyMachineryById(id)
      .then(setSpace)
      .finally(() => setLoading(false));
    getReviewsForSpace(id)
      .then(setReviews)
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEWS_LOAD_ERROR)))
      .finally(() => setReviewsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // "Configuración > Privacidad > Espacios publicados" -- no react-helmet in
  // this app, so this is a direct, cleaned-up-on-unmount DOM tag instead.
  useEffect(() => {
    if (space?.includeInSearchEngines === false) {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex';
      document.head.appendChild(meta);
      return () => {
        document.head.removeChild(meta);
      };
    }
    return undefined;
  }, [space]);

  if (loading) {
    return withLayout(
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!space) {
    return withLayout(
      <EmptyState
        icon={<IconMoodSad size={24} />}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_NOT_FOUND)}
        description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_NOT_FOUND_DESCRIPTION)}
      />
    );
  }

  // The Draft/PendingVerification/Active/... status only means something to
  // the host managing their own listing -- a guest browsing a listed space
  // already knows it's listed, so showing "Active" to them is just noise.
  const isOwner = !!userId && userId === space.hostUserId;

  // Same condition the booking widget itself uses to render at all (a
  // Draft/Suspended/... listing has nothing bookable below) -- and never for
  // the host previewing their own listing, who can't book their own machine.
  const showReserveButtons = space.status === 'Active' && !isOwner;

  const goToBooking = () => navigate(`/heavy-duty-machinery/${space.id}/book`);

  const optionLabel = (value: string | undefined, options: HeavyDutyMachineryOption[]) => {
    if (!value) return '—';
    return translate(t, options.find((o) => o.value === value)?.labelKey ?? value);
  };

  const priceLabel =
    space.rentalMode === 'Overnight'
      ? space.pricePerNight != null && `${formatCurrency(space.pricePerNight, space.hostCurrencyCode, i18n.language)} ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_NIGHT)}`
      : space.pricePerHour != null && `${formatCurrency(space.pricePerHour, space.hostCurrencyCode, i18n.language)} ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_HOUR)}`;
  // Secondary, smaller/greyer approximation next to the host's own price
  // above -- only meaningful for a guest (not the host viewing their own
  // listing) whose own preference differs from this machine's
  // HostCurrencyCode. See useFxConvertedAmount/FxRateController's own doc
  // comments for why this is display-only.
  const convertedPriceLabel =
    !isOwner && convertedPriceAmount != null && userProfile?.preferredCurrencyCode
      ? formatCurrency(convertedPriceAmount, userProfile.preferredCurrencyCode, i18n.language)
      : undefined;

  const photos = space.photos ?? [];
  const heroPhoto = photos[0];
  const thumbnails = photos.slice(1, 1 + MAX_GALLERY_THUMBNAILS);
  const overflowCount = Math.max(0, photos.length - 1 - MAX_GALLERY_THUMBNAILS);

  const brandModelYear = [
    space.manufacturerBrand,
    space.modelName,
    space.yearManufactured != null ? `(${space.yearManufactured})` : undefined,
  ].filter(Boolean).join(' ');

  const featureCards: { icon: React.ReactNode; labelKey: string; value: React.ReactNode }[] = [
    {
      icon: <IconTag size={22} />,
      labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CATEGORY_LABEL,
      value: resolveCategoryName(space.category),
    },
    {
      icon: <IconEngine size={22} />,
      labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MANUFACTURER_BRAND_LABEL,
      value: brandModelYear || '—',
    },
    {
      icon: <IconWeight size={22} />,
      labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WEIGHT_TONS_LABEL,
      value: space.weightTons != null ? `${space.weightTons} t` : '—',
    },
    {
      icon: <IconGauge size={22} />,
      labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_POWER_HP_LABEL,
      value: space.powerHp != null ? `${space.powerHp} hp` : '—',
    },
    {
      icon: <IconArrowsMaximize size={22} />,
      labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_REACH_METERS_LABEL,
      value: space.reachMeters != null ? `${space.reachMeters} m` : '—',
    },
    {
      icon: <IconCalendarStats size={22} />,
      labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_ENGINE_HOURS_LABEL,
      value: space.engineHoursAtListing != null ? `${space.engineHoursAtListing} h` : '—',
    },
    {
      icon: <IconKey size={22} />,
      labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_ACCESS_TYPE_LABEL,
      value: optionLabel(space.accessType, ACCESS_TYPE_OPTIONS),
    },
    {
      icon: <IconUsers size={22} />,
      labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_OPERATORS_LABEL,
      value: space.maxOperators,
    },
    {
      icon: <IconCalendarOff size={22} />,
      labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CANCELLATION_POLICY_LABEL,
      value: (
        <>
          {optionLabel(space.cancellationPolicy, CANCELLATION_POLICY_OPTIONS)}
          {' — '}
          {CANCELLATION_WINDOW_HOURS[space.cancellationPolicy] != null
            ? `${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CANCELLATION_FREE_UNTIL)} ${CANCELLATION_WINDOW_HOURS[space.cancellationPolicy]}h ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CANCELLATION_BEFORE_BOOKING_SUFFIX)}`
            : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CANCELLATION_NON_REFUNDABLE)}
        </>
      ),
    },
  ];

  // Nivel A global specs -- appended (not spliced in) since these are all
  // optional and absent on most pre-existing listings, unlike the fields
  // above; only pushed when actually set, same "don't show a blank card"
  // convention as verifiedBadge's Pill above.
  if (space.propulsionType) {
    featureCards.push({
      icon: <IconSteeringWheel size={22} />,
      labelKey: TRANSLATIONS.MACHINERY_SPEC_PROPULSION_TYPE_LABEL,
      value: optionLabel(space.propulsionType, PROPULSION_TYPE_OPTIONS),
    });
  }
  if (space.fuelType) {
    featureCards.push({
      icon: <IconGasStation size={22} />,
      labelKey: TRANSLATIONS.MACHINERY_SPEC_FUEL_TYPE_LABEL,
      value: optionLabel(space.fuelType, FUEL_TYPE_OPTIONS),
    });
  }
  if (space.transportLengthM != null) {
    featureCards.push({
      icon: <IconRulerMeasure size={22} />,
      labelKey: TRANSLATIONS.MACHINERY_SPEC_TRANSPORT_LENGTH_LABEL,
      value: `${space.transportLengthM} m`,
    });
  }
  if (space.transportWidthM != null) {
    featureCards.push({
      icon: <IconRulerMeasure size={22} />,
      labelKey: TRANSLATIONS.MACHINERY_SPEC_TRANSPORT_WIDTH_LABEL,
      value: `${space.transportWidthM} m`,
    });
  }
  if (space.transportHeightM != null) {
    featureCards.push({
      icon: <IconRulerMeasure size={22} />,
      labelKey: TRANSLATIONS.MACHINERY_SPEC_TRANSPORT_HEIGHT_LABEL,
      value: `${space.transportHeightM} m`,
    });
  }
  if (space.hasQuickHitch) {
    featureCards.push({
      icon: <IconPlugConnected size={22} />,
      labelKey: TRANSLATIONS.MACHINERY_SPEC_HAS_QUICK_HITCH_LABEL,
      value: <IconCircleCheck size={18} color={token.colorSuccess} />,
    });
  }

  // Nivel B per-type specs -- only the 20 types machineryCustomSpecFieldsByType.ts
  // has a schema for resolve labels/units for whatever keys are actually
  // present in the opaque CustomAttributesJson blob; every other type (or a
  // listing with nothing filled in) renders no section at all.
  const customSpecFields = getCustomSpecFields(space.category);
  let parsedCustomSpecs: Record<string, string | number | boolean> = {};
  if (space.customAttributesJson) {
    try {
      parsedCustomSpecs = JSON.parse(space.customAttributesJson);
    } catch {
      parsedCustomSpecs = {};
    }
  }
  const customSpecCards = customSpecFields
    .filter((field) => parsedCustomSpecs[field.key] !== undefined && parsedCustomSpecs[field.key] !== null && parsedCustomSpecs[field.key] !== '')
    .map((field) => {
      const rawValue = parsedCustomSpecs[field.key];
      let displayValue: React.ReactNode;
      if (field.type === 'boolean') {
        displayValue = rawValue ? <IconCircleCheck size={18} color={token.colorSuccess} /> : '—';
      } else if (field.type === 'select') {
        displayValue = translate(t, field.options?.find((o) => o.value === rawValue)?.labelKey ?? String(rawValue));
      } else if (field.type === 'number') {
        displayValue = field.unitKey ? `${rawValue} ${translate(t, field.unitKey)}` : String(rawValue);
      } else {
        displayValue = String(rawValue);
      }
      return { key: field.key, labelKey: field.labelKey, value: displayValue };
    });

  return withLayout(
    <div
      style={{
        width: '100%',
        maxWidth: 960,
        textAlign: 'left',
        // Room for the floating mobile "Reservar ahora" bar below so it
        // never ends up covering the page's own last bit of content
        // (reviews) once scrolled all the way down.
        paddingBottom: isMobile && showReserveButtons ? 96 : undefined,
      }}
    >
      {heroPhoto && (
        <Image.PreviewGroup items={photos}>
          <div
            style={{
              display: 'flex',
              gap: 8,
              // aspectRatio (not a fixed px height) so the row's actual
              // height always tracks its own width -- a fixed 320px stayed
              // that tall even as the row itself narrowed down to a phone
              // screen, cropping the hero photo into an increasingly
              // tall/narrow sliver instead of a consistent, favorable shape.
              aspectRatio: '16 / 9',
              marginBottom: 24,
            }}
          >
            <div style={{ flex: thumbnails.length > 0 ? 2 : 1, overflow: 'hidden', borderRadius: 12 }}>
              <Image src={heroPhoto} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            {thumbnails.length > 0 && (
              <div
                style={{
                  flex: 1,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gridTemplateRows: '1fr 1fr',
                  gap: 8,
                }}
              >
                {thumbnails.map((url, index) => (
                  <div key={url} style={{ position: 'relative', overflow: 'hidden', borderRadius: 8 }}>
                    <Image src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {index === thumbnails.length - 1 && overflowCount > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(0,0,0,0.55)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          pointerEvents: 'none',
                        }}
                      >
                        +{overflowCount} {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MORE_PHOTOS_SUFFIX)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Image.PreviewGroup>
      )}
      {/* Never for the owner (can't report your own listing's photos) or a
          signed-out visitor (reporting requires auth) -- a plain text link
          rather than an icon-only button, since it sits in otherwise-empty
          space right under the gallery with nothing else to visually anchor
          a bare icon to. */}
      {photos.length > 0 && isAuthenticated && !isOwner && (
        <div style={{ textAlign: 'right', marginTop: -16, marginBottom: 16 }}>
          <Text
            type="secondary"
            underline
            style={{ fontSize: 12, cursor: 'pointer' }}
            onClick={() => setReportPhotoPickerOpen(true)}
          >
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_REPORT_PHOTO_LINK)}
          </Text>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Space size={8}>
            {space.telemetry &&
              isPublicMachineryTelemetry(space.telemetry) &&
              (() => {
                const LiveStatusIcon = LIVE_STATUS_ICONS[space.telemetry!.status] ?? IconEngine;
                return (
                  <Pill tone={MACHINERY_LIVE_STATUS_TONES[space.telemetry!.status] ?? 'default'} icon={<LiveStatusIcon size={14} />}>
                    {translate(t, MACHINERY_LIVE_STATUS_LABEL_KEYS[space.telemetry!.status] ?? space.telemetry!.status)}
                  </Pill>
                );
              })()}
            {isOwner &&
              (() => {
                const StatusIcon = STATUS_ICONS[space.status] ?? IconFileText;
                return (
                  <Pill tone={HEAVY_DUTY_MACHINERY_SPACE_STATUS_TONES[space.status] ?? 'default'} icon={<StatusIcon size={14} />}>
                    {translate(t, HEAVY_DUTY_MACHINERY_SPACE_STATUS_LABEL_KEYS[space.status] ?? space.status)}
                  </Pill>
                );
              })()}
            {space.verifiedBadge && (
              <Pill tone="success" icon={<IconCircleCheck size={14} />}>
                {translate(t, TRANSLATIONS.HOST_VERIFICATION_VERIFIED)}
              </Pill>
            )}
          </Space>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 4px' }}>
            <BackButton fallbackPath="/heavy-duty-machinery" />
            <Title level={3} style={{ margin: 0 }}>
              {space.title}
            </Title>
          </div>
          <Space size={4}>
            <IconMapPin size={16} />
            <Text type="secondary">
              {space.city}, {space.province ? `${space.province}, ` : ''}
              {space.country}
            </Text>
          </Space>
          {isAuthenticated && !isOwner && (
            <div style={{ marginTop: 4 }}>
              <Button
                type="text"
                size="small"
                icon={<IconFlag size={14} />}
                style={{ paddingLeft: 0 }}
                onClick={() => setReportSpaceOpen(true)}
              >
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_REPORT_BUTTON)}
              </Button>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', width: isMobile ? '100%' : undefined }}>
          {priceLabel && (
            <Title level={3} style={{ margin: 0 }}>
              {priceLabel}
            </Title>
          )}
          {convertedPriceLabel && (
            <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
              ≈ {convertedPriceLabel}
            </Text>
          )}
          {space.averageRating != null && (
            <Space size={4}>
              <Rate disabled allowHalf value={space.averageRating} style={{ fontSize: 14 }} />
              <Text strong>{space.averageRating.toFixed(1)}</Text>
              <Text type="secondary">({space.reviewCount})</Text>
            </Space>
          )}
          {/* Desktop only -- mobile gets a single floating "Reservar ahora"
              bar instead (see below), always reachable above the fold
              rather than two inline CTAs that could each scroll out of view. */}
          {showReserveButtons && !isMobile && (
            <div style={{ marginTop: 12 }}>
              <ReserveButton onClick={goToBooking}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RESERVE_BUTTON)}
              </ReserveButton>
            </div>
          )}
        </div>
      </div>

      <Paragraph style={{ marginTop: 16 }}>{space.description}</Paragraph>

      {space.hostAggregatedRating != null && (
        <Card size="small" style={{ margin: '16px 0 24px' }}>
          {/* Anonymous provider-level track record -- no name, photo, or
              link (see HostsController's own doc comment on why the public
              host profile is gone). Both numbers are aggregated across
              every space this provider has, not just this one, via
              HostStatsHelper -- the rating from Reviews, the count from
              actual completed Bookings (not reviews left, which is
              usually a smaller number). */}
          <Space size={8} align="center">
            <Rate disabled allowHalf value={space.hostAggregatedRating} style={{ fontSize: 16 }} />
            <Text strong style={{ fontSize: 16 }}>
              {space.hostAggregatedRating.toFixed(1)}
            </Text>
            <Text type="secondary">
              · {space.hostCompletedRentalsCount} {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_HOST_COMPLETED_RENTALS_LABEL)}
            </Text>
          </Space>
        </Card>
      )}

      <Title level={4}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_FEATURES_SECTION_TITLE)}</Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {featureCards.map((feature) => (
          <Col xs={12} sm={8} key={feature.labelKey}>
            <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }} style={{ height: '100%' }}>
              {/* Plain flex divs here instead of antd <Space>, which defaults
                  to display: inline-flex -- sized to fit its content rather
                  than the card's actual width, so a long label/value just
                  grew the row past the card's edge instead of wrapping.
                  width: '100%' + minWidth: 0 on the label text is what lets
                  it shrink and wrap onto a second line like the values
                  already did. */}
              <Card size="small" style={{ height: '100%', borderRadius: 18, overflow: 'hidden' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                    <span style={{ flexShrink: 0 }}>{feature.icon}</span>
                    <Text type="secondary" style={{ minWidth: 0, flex: 1, wordBreak: 'break-word' }}>
                      {translate(t, feature.labelKey)}
                    </Text>
                  </div>
                  <div style={{ wordBreak: 'break-word' }}>{feature.value}</div>
                </div>
              </Card>
            </motion.div>
          </Col>
        ))}
      </Row>

      {customSpecCards.length > 0 && (
        <>
          <Title level={4}>{translate(t, TRANSLATIONS.MACHINERY_SPEC_DETAIL_SECTION_TITLE)}</Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {customSpecCards.map((spec) => (
              <Col xs={12} sm={8} key={spec.key}>
                <Card size="small" style={{ height: '100%', borderRadius: 18, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                    <Text type="secondary" style={{ wordBreak: 'break-word' }}>
                      {translate(t, spec.labelKey)}
                    </Text>
                    <div style={{ wordBreak: 'break-word' }}>{spec.value}</div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {(space.dryHireAvailable || space.wetHireHostOperatorAvailable || space.wetHireThirdPartyAvailable || space.deliveryAvailable) && (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RENTAL_AVAILABILITY_LABEL)}</Title>
          <Space size={8} wrap>
            {space.dryHireAvailable && (
              <Pill tone="default" icon={<IconSteeringWheel size={14} />}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_DRY)}
              </Pill>
            )}
            {space.wetHireHostOperatorAvailable && (
              <Pill tone="default" icon={<IconUserCog size={14} />}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_WET_HOST)}
              </Pill>
            )}
            {space.wetHireThirdPartyAvailable && (
              <Pill tone="default" icon={<IconUserBolt size={14} />}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_WET_THIRD_PARTY)}
              </Pill>
            )}
            {space.deliveryAvailable && (
              <Pill tone="default" icon={<IconTruckDelivery size={14} />}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_DELIVERY)}
              </Pill>
            )}
          </Space>
          {space.overtimeHourRate != null && (
            <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
              <IconCoin size={16} style={{ position: 'relative', top: 2, marginRight: 6 }} />
              <Text type="secondary">
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_OVERTIME_HOUR_RATE_LABEL)}: {space.overtimeHourRate.toFixed(2)}€/h
              </Text>
            </Paragraph>
          )}
        </div>
      )}

      {space.privateAddress && (
        <Paragraph style={{ marginTop: 8 }}>
          <Text strong>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_EXACT_ADDRESS_LABEL)}: </Text>
          {space.privateAddress}
        </Paragraph>
      )}

      {showReserveButtons && !isMobile && (
        <div style={{ marginTop: 16, marginBottom: 8, textAlign: 'center' }}>
          <ReserveButton size="large" onClick={goToBooking} style={{ width: 320 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RESERVE_NOW_BUTTON)}
          </ReserveButton>
        </div>
      )}

      {/* Mobile's single "Reservar ahora" CTA -- fixed just above
          MobileBottomNav (flush against its top edge, see
          MOBILE_BOTTOM_NAV_HEIGHT) when signed in, or at the real screen
          bottom when signed out (LandingLayout has no bottom nav to clear).
          Slides away together with that bar on scroll-down and back on
          scroll-up (same transform/opacity/pointerEvents recipe, same
          useScrollChrome source) rather than staying pinned over content
          while the guest is actively reading. */}
      {showReserveButtons && isMobile && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: isAuthenticated ? MOBILE_BOTTOM_NAV_HEIGHT : 'env(safe-area-inset-bottom, 0px)',
            zIndex: 999,
            padding: '10px 16px',
            background: token.colorBgContainer,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
            transform: scrollingDown ? 'translateY(100%)' : 'translateY(0)',
            opacity: scrollingDown ? 0 : 1,
            transition: 'transform 220ms ease, opacity 220ms ease',
            pointerEvents: scrollingDown ? 'none' : undefined,
          }}
        >
          <ReserveButton block size="large" onClick={goToBooking}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RESERVE_NOW_BUTTON)}
          </ReserveButton>
        </div>
      )}

      <Divider />
      <Title level={4}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEWS_SECTION_TITLE)}</Title>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        {space.averageRating != null ? (
          <>
            <Rate disabled allowHalf value={space.averageRating} />
            <Text strong>{space.averageRating.toFixed(1)}</Text>
            <Text type="secondary">
              ({space.reviewCount} {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEWS_COUNT_LABEL)})
            </Text>
          </>
        ) : (
          <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEWS_NO_RATING_YET)}</Text>
        )}
      </div>

      {reviewsLoading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : reviews.length === 0 ? (
        <EmptyState
          icon={<IconStar size={24} />}
          title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEWS_EMPTY)}
          description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEWS_EMPTY_DESCRIPTION)}
        />
      ) : (
        <List
          itemLayout="vertical"
          dataSource={reviews}
          renderItem={(review) => (
            <List.Item key={review.id}>
              <List.Item.Meta
                title={<Rate disabled value={review.rating} />}
                description={
                  <>
                    {dayjs(review.createdAt).format('DD/MM/YYYY')}
                    {review.reviewerCountry && ` · ${review.reviewerCountry}`}
                    {review.stayDurationNights != null &&
                      review.stayDurationNights > 0 &&
                      ` · ${review.stayDurationNights} ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEWS_NIGHTS_SUFFIX)}`}
                  </>
                }
              />
              {review.comment && <Paragraph>{review.comment}</Paragraph>}
              {review.hostResponse && (
                <div style={{ background: token.colorBorderSecondary, borderRadius: 8, padding: 12, marginTop: 8 }}>
                  <Text strong>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEWS_HOST_RESPONSE_LABEL)}: </Text>
                  <Text>{review.hostResponse}</Text>
                </div>
              )}
            </List.Item>
          )}
        />
      )}

      <ReportModal open={reportSpaceOpen} targetType="Space" targetId={space.id} onClose={() => setReportSpaceOpen(false)} />

      {/* Picker step before ReportModal itself -- reporting a photo needs to
          know WHICH one, unlike Space (there's only one target on this whole
          page) or a chat message (already scoped to the one bubble tapped). */}
      <Modal
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_REPORT_PHOTO_MODAL_TITLE)}
        open={reportPhotoPickerOpen}
        onCancel={() => setReportPhotoPickerOpen(false)}
        footer={null}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {photos.map((url) => (
            <button
              key={url}
              type="button"
              onClick={() => {
                setReportPhotoPickerOpen(false);
                setReportPhotoUrl(url);
              }}
              style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 8, overflow: 'hidden' }}
            >
              <img src={url} alt="" style={{ width: 88, height: 88, objectFit: 'cover', display: 'block' }} />
            </button>
          ))}
        </div>
      </Modal>
      {reportPhotoUrl && (
        <ReportModal
          open
          targetType="Photo"
          targetId={reportPhotoUrl}
          onClose={() => setReportPhotoUrl(undefined)}
        />
      )}
    </div>
  );
};

export default HeavyDutyMachineryDetail;
